import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Period {
  start: string; // ISO string
  end: string;   // ISO string
  preset?: '24h' | '7d' | '30d' | 'prev1m' | 'custom';
}

interface PeriodSelectorProps {
  value: Period;
  onChange: (period: Period) => void;
}

function presetToPeriod(preset: '24h' | '7d' | '30d' | 'prev1m'): Period {
  const now = new Date();
  if (preset === 'prev1m') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end   = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: end.toISOString(), preset };
  }
  const end = now;
  const start = new Date();
  if (preset === '24h') start.setHours(start.getHours() - 24);
  else if (preset === '7d') start.setDate(start.getDate() - 7);
  else start.setDate(start.getDate() - 30);
  return { start: start.toISOString(), end: end.toISOString(), preset };
}

function toDateInputValue(iso: string): string {
  return iso.slice(0, 10); // YYYY-MM-DD
}

export function makeDefaultPeriod(): Period {
  return presetToPeriod('24h');
}

export default function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const { t } = useTranslation();
  const [showCustom, setShowCustom] = useState(value.preset === 'custom');
  const [customStart, setCustomStart] = useState(toDateInputValue(value.start));
  const [customEnd, setCustomEnd] = useState(toDateInputValue(value.end));
  const [customError, setCustomError] = useState('');

  const handlePreset = (preset: '24h' | '7d' | '30d' | 'prev1m') => {
    setShowCustom(false);
    setCustomError('');
    onChange(presetToPeriod(preset));
  };

  const handleCustomToggle = () => {
    setShowCustom(true);
    onChange({ ...value, preset: 'custom' });
  };

  const handleCustomApply = () => {
    setCustomError('');
    const start = new Date(customStart);
    const end = new Date(customEnd + 'T23:59:59');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setCustomError(t('chart.customInvalidDate'));
      return;
    }
    if (end <= start) {
      setCustomError(t('chart.customEndBeforeStart'));
      return;
    }
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 365) {
      setCustomError(t('chart.customMaxOneYear'));
      return;
    }
    onChange({ start: start.toISOString(), end: end.toISOString(), preset: 'custom' });
  };

  const btnClass = (active: boolean) =>
    `px-3 py-1.5 text-sm rounded border transition-colors ${
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button className={btnClass(value.preset === '24h')} onClick={() => handlePreset('24h')}>
          {t('chart.last24h')}
        </button>
        <button className={btnClass(value.preset === '7d')} onClick={() => handlePreset('7d')}>
          {t('chart.last7d')}
        </button>
        <button className={btnClass(value.preset === '30d')} onClick={() => handlePreset('30d')}>
          {t('chart.last30d')}
        </button>
        <button className={btnClass(value.preset === 'prev1m')} onClick={() => handlePreset('prev1m')}>
          {t('chart.prevMonth')}
        </button>
        <button className={btnClass(value.preset === 'custom')} onClick={handleCustomToggle}>
          {t('chart.customPeriod')}
        </button>
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-sm text-gray-600">{t('chart.periodFrom')}</span>
          <input
            type="date"
            value={customStart}
            max={customEnd}
            onChange={(e) => setCustomStart(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <span className="text-sm text-gray-600">{t('chart.periodTo')}</span>
          <input
            type="date"
            value={customEnd}
            min={customStart}
            max={toDateInputValue(new Date().toISOString())}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <button
            onClick={handleCustomApply}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t('chart.apply')}
          </button>
          {customError && <span className="text-xs text-red-600">{customError}</span>}
        </div>
      )}
    </div>
  );
}
